import { UserRole } from '@today/types'
import type { PlaneTarget } from '@today/ui-components'
import { roleAtLeast } from '@/auth/role-order'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { useViewerRole } from '@/auth/useViewerRole'

/** The three authenticated planes (AD-4). Stable ids reported by the switcher. */
export enum PlaneId {
	Platform = 'platform',
	TenantAdmin = 'tenant-admin',
	Workspace = 'workspace',
}

const PLANE_LABELS: Record<PlaneId, string> = {
	[PlaneId.Platform]: 'Platform Console',
	[PlaneId.TenantAdmin]: 'Tenant Admin',
	[PlaneId.Workspace]: 'Workspace',
}

/**
 * The planes a viewer of this role may open, per `frontend-architecture.md` §3:
 *
 * - SUPER_ADMIN / PLATFORM_ADMIN → Platform Console + their own Tenant Admin and
 *   Workspace planes (cross-tenant "enter", AD-5, is out of scope — TO-634).
 * - ORG_ADMIN / ORG_MANAGER / ADMIN → Tenant Admin + Workspace of their tenant.
 * - USER / VIEWER → Workspace only.
 *
 * The targets carry no `requiredAtom`: reachability is decided here by the coarse
 * role ladder, so the switcher renders exactly what this returns.
 */
export function planeTargetsForRole(role: UserRole): PlaneTarget[] {
	const ids: PlaneId[] = []
	if (roleAtLeast(role, UserRole.PLATFORM_ADMIN)) ids.push(PlaneId.Platform)
	if (roleAtLeast(role, UserRole.ADMIN)) ids.push(PlaneId.TenantAdmin)
	ids.push(PlaneId.Workspace)
	return ids.map((id) => ({ id, label: PLANE_LABELS[id] }))
}

/**
 * The base path a chosen plane navigates to. The Tenant Admin plane is keyed by
 * the viewer's own tenant slug (`/t/{slug}/admin`); without a slug it falls back
 * to the workspace dashboard (the switcher never changes the tenant — AD-8).
 */
export function planeBasePath(plane: PlaneId, tenantSlug: string | null): string {
	switch (plane) {
		case PlaneId.Platform:
			return '/admin'
		case PlaneId.TenantAdmin:
			return tenantSlug ? `/t/${tenantSlug}/admin` : '/dashboard'
		case PlaneId.Workspace:
			return '/dashboard'
	}
}

/** The viewer's reachable plane targets and the own-tenant slug that resolves their paths. */
export interface PlaneTargetsState {
	targets: PlaneTarget[]
	tenantSlug: string | null
}

/** Binds {@link planeTargetsForRole} to the signed-in viewer. */
export function usePlaneTargets(): PlaneTargetsState {
	const { role } = useViewerRole()
	const { tenantSlug } = useViewerPermissions()
	return { targets: planeTargetsForRole(role), tenantSlug }
}
